export interface DbNameAndAlias {
  /** Backend-canonical database name. */
  tenantDatabaseName?: string | null;

  /** Optional alias from CustomerDatabase.DBListAlias. */
  databaseAlias?: string | null;
}

/**
 * Renders a database to a UI string: `alias ?? name`, both trimmed.
 *
 * Display-only — never pass the return value back to the BE as an identifier
 * or filter value; use `tenantDatabaseName` for that.
 *
 * Mirrors Marcelo's `feat/eum-authorization-policies` FE plan §4 spec; if his
 * branch lands a canonical helper elsewhere first, consolidate to that location.
 */
export function dbDisplayName(row: DbNameAndAlias): string {
  const alias = row.databaseAlias?.trim();
  if (alias) return alias;
  return row.tenantDatabaseName?.trim() ?? '';
}

/** Approvals wire uses `dbName` / `dbAlias` siblings instead of `tenantDatabaseName` / `databaseAlias`. */
export function approvalDbDisplayName(row: { dbName?: string | null; dbAlias?: string | null }): string {
  const alias = row.dbAlias?.trim();
  if (alias) return alias;
  return row.dbName?.trim() ?? '';
}

/** Canonical tenant name for tooltips when the visible label is an alias. */
export function dbCanonicalName(row: DbNameAndAlias): string {
  return row.tenantDatabaseName?.trim() ?? '';
}

export function approvalDbCanonicalName(row: { dbName?: string | null; dbAlias?: string | null }): string {
  return row.dbName?.trim() ?? '';
}

/** Database picker row: prefer `alias`, fall back to legacy `description`, never sent to BE as identifier. */
export function databasePickerLabel(db: { name: string; alias?: string | null; description?: string | null }): string {
  const alias = db.alias?.trim() ?? db.description?.trim() ?? null;
  return alias ? `${alias} (${db.name})` : db.name;
}

type DatabaseAliasLookupRow = {
  name?: string | null;
  alias?: string | null;
  databaseAlias?: string | null;
  tenantDatabaseName?: string | null;
};

/** Case-insensitive alias lookup for canonical database names (display-only). */
export function lookupDatabaseAlias(name: string, catalog: ReadonlyArray<DatabaseAliasLookupRow>): string | null {
  const key = name.trim().toLowerCase();
  if (key === '') return null;
  const entry = catalog.find((row) => {
    const canonical = (row.name ?? row.tenantDatabaseName ?? '').trim().toLowerCase();
    return canonical === key;
  });
  if (!entry) return null;
  const alias = entry.alias ?? entry.databaseAlias ?? null;
  const trimmed = alias?.trim();
  return trimmed ? trimmed : null;
}

/** Builds display labels for canonical names; values stay canonical for API writes. */
export function databaseDisplayLabelsByCanonicalName(
  names: readonly string[],
  catalog: ReadonlyArray<DatabaseAliasLookupRow>
): Map<string, string> {
  return buildDatabaseDisplayLabelsByName(names, { databaseCatalog: catalog });
}

export interface DatabaseDisplayLabelSources {
  /** User profile shards from GET /users/{username}. */
  tenantDatabaseEntries?: readonly DbNameAndAlias[];
  /** Grid row snapshot (list API databases[] with aliases). */
  seedDatabaseEntries?: readonly DbNameAndAlias[];
  /** Picker catalog from useGetUserDatabases. */
  databaseCatalog?: readonly DatabaseAliasLookupRow[];
  /** Full tenant catalog from GET /databases. */
  fullCatalog?: readonly DatabaseAliasLookupRow[];
}

function aliasFromTenantEntries(name: string, entries: readonly DbNameAndAlias[]): string | null {
  const key = name.trim().toLowerCase();
  if (key === '') return null;
  for (const entry of entries) {
    const canonical = entry.tenantDatabaseName?.trim().toLowerCase();
    if (canonical !== key) continue;
    const alias = entry.databaseAlias?.trim();
    if (alias) return alias;
  }
  return null;
}

function resolveDatabaseAlias(name: string, sources: DatabaseDisplayLabelSources): string | null {
  const fromProfile = aliasFromTenantEntries(name, sources.tenantDatabaseEntries ?? []);
  if (fromProfile) return fromProfile;

  const fromSeed = aliasFromTenantEntries(name, sources.seedDatabaseEntries ?? []);
  if (fromSeed) return fromSeed;

  const fromUserCatalog = lookupDatabaseAlias(name, sources.databaseCatalog ?? []);
  if (fromUserCatalog) return fromUserCatalog;

  return lookupDatabaseAlias(name, sources.fullCatalog ?? []);
}

/**
 * Builds display labels for canonical database names using multiple alias sources.
 * Values stay canonical for API writes; labels use `dbDisplayName` (alias ?? name).
 */
export function buildDatabaseDisplayLabelsByName(names: readonly string[], sources: DatabaseDisplayLabelSources): Map<string, string> {
  return new Map(
    names.map((name) => [name, dbDisplayName({ tenantDatabaseName: name, databaseAlias: resolveDatabaseAlias(name, sources) })])
  );
}
